import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiAlertTriangle, FiLock, FiDatabase, FiCloudOff, FiEyeOff, FiServer, FiShield, FiUserX, FiGitBranch, FiTerminal, FiMail, FiFileText } = FiIcons;

const ProblemSection = () => {
  useEffect(() => {
    // SEO: Add comprehensive structured data for problem section
    const problemData = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Documented Public AI Security Breaches: March 2023 - December 2025 Comprehensive Analysis",
      "description": "Factual incidents from OpenAI, Microsoft, Google, and Meta showing real dangers of public AI clouds including Redis bugs, data leaks, and prompt injection vulnerabilities",
      "author": {
        "@type": "Organization", 
        "name": "Secure Enterprise AI Solutions"
      },
      "datePublished": "2024-01-15",
      "dateModified": "2025-01-15",
      "about": [
        "AI Security", 
        "Data Breaches", 
        "Cloud Computing Risks", 
        "Enterprise Security",
        "OpenAI Security Issues",
        "Microsoft Azure Breaches",
        "Google Gemini Vulnerabilities",
        "Meta Privacy Concerns"
      ],
      "mentions": [
        "OpenAI ChatGPT",
        "Microsoft Azure", 
        "Google Gemini",
        "Meta AI",
        "GPT-5",
        "LLM Security"
      ]
    };
    
    const scriptId = 'problem-structured-data';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(problemData);
  }, []);
  
  const incidents = [
    {
      category: "OpenAI / ChatGPT Incidents",
      icon: FiDatabase,
      incidents: [
        {
          date: "March 20–21, 2023",
          title: "ChatGPT Data Leak (Redis Bug)",
          description: "A bug in the Redis client library used by OpenAI caused requests to return cached data from other users' sessions, exposing other users' chat titles and, for some Plus subscribers, billing data on the 'Manage Subscription' page.",
          dataExposed: "For about 1.2% of active ChatGPT Plus users during a ~9‑hour window, other users could see first and last names, email and payment addresses, credit card type, last four digits, and expiration date; full card numbers were not exposed.",
          impactOnOpenAI: "Service was taken offline, history disabled for part of the day, public disclosure, regulatory and media scrutiny, and subsequent launch of a formal bug bounty program.",
          impactOnUsers: "Confidentiality breach of personal and limited payment data; users had no control once data was in the provider's environment, illustrating multi‑tenant risk and third‑party dependency.",
          severity: "HIGH"
        },
        {
          date: "Early 2023",
          title: "Undisclosed OpenAI Internal Breach", 
          description: "An external actor accessed OpenAI's internal employee messaging systems and obtained information about AI system designs, but not the core production systems hosting models and customer data.",
          dataExposed: "Internal AI design details and confidential R&D information from employee messaging systems.",
          impactOnOpenAI: "Loss of confidential R&D information, internal concern about nation‑state espionage, and criticism for not notifying law enforcement or disclosing the incident publicly at the time.",
          impactOnUsers: "No direct customer data loss was reported, but the incident highlights that compromise of internal collaboration systems can yield detailed knowledge useful for future targeted attacks on AI infrastructure.",
          severity: "MEDIUM"
        },
        {
          date: "2023",
          title: "ChatGPT Plugin Vulnerabilities",
          description: "Security researchers found several OAuth‑related flaws in the ChatGPT plugin ecosystem that allowed installation of malicious plugins on victim accounts via crafted links, causing all future messages (including secrets) to be sent to attackers.",
          dataExposed: "Full takeover of GitHub accounts via AskTheCode plugin vulnerability; account takeover in other services through OAuth misconfiguration.",
          impactOnOpenAI: "Required fixes to OpenAI's own plugin handling and coordination with third‑party plugin developers; demonstrated that extending cloud AI with external connectors expands the attack surface substantially.",
          impactOnUsers: "Potential exposure of sensitive messages entered into ChatGPT, compromise of connected third‑party accounts (e.g. GitHub), and downstream code or data exfiltration risks.",
          severity: "HIGH"
        },
        {
          date: "2024",
          title: "Prompt‑Injection Exfiltration from ChatGPT",
          description: "Academic researchers demonstrated that ChatGPT‑4 and 4o can be manipulated via adversarial prompts to leak personal information stored in the system's 'memory' or conversation context, even without third‑party tools.",
          dataExposed: "Personal information stored in long-term memory and conversation context.",
          impactOnOpenAI: "Shows that even with access controls, model‑level behavior can violate confidentiality if personal data is stored in long‑term memory and not properly compartmentalized.",
          impactOnUsers: "If users rely on AI memory or integrate it with enterprise data, crafted prompts can silently exfiltrate sensitive information, making confidentiality dependent on model alignment rather than traditional access control.",
          severity: "MEDIUM"
        },
        {
          date: "2025",
          title: "GPT‑5 Jailbreaks and Security Concerns",
          description: "Security firms showed that narrative‑based jailbreaks could still bypass GPT‑5's safety systems, and that autonomous AI agents connected to cloud data sources can be manipulated via prompt injection to leak sensitive data without user interaction.",
          dataExposed: "Sensitive data from autonomous AI agents connected to cloud data sources.",
          impactOnOpenAI: "Undermines marketing claims of 'secure by default' and requires continuous red‑teaming and guardrail updates for new model releases.",
          impactOnUsers: "Highlights that upgrading to more capable cloud AI models may increase exposure to sophisticated adversarial prompts, especially in agentic workflows tied to enterprise data.",
          severity: "HIGH"
        }
      ]
    },
    {
      category: "Microsoft Azure / Copilot / AI Research Incidents",
      icon: FiCloudOff,
      incidents: [
        {
          date: "June 2023 - September 18, 2023",
          title: "Microsoft AI GitHub Exposure: 38 TB of Internal Data",
          description: "AI researchers published an Azure Storage URL (SAS token) in a public GitHub repo to share open‑source training data, but the token was misconfigured to grant full‑control access to the entire storage account, exposing ~38 TB of internal data.",
          dataExposed: "Backups of employee workstations, 30,000+ internal Teams messages, passwords, secret keys, and other sensitive internal information.",
          impactOnMicrosoft: "Required revocation of the token, internal review of SAS usage, and public disclosure; raised questions about secure handling of training data and secrets in AI development environments.",
          impactOnUsers: "While data belonged to Microsoft, not customers, the incident illustrates that cloud AI training pipelines can inadvertently expose internal credentials and communications, which could then be leveraged to attack customer‑facing services.",
          severity: "HIGH"
        },
        {
          date: "2025",
          title: "General Risks in AI Training Environments",
          description: "'2025 State of Data Compliance and Security Report' highlighting AI model training risks showing that a majority of organizations report breaches or theft in environments used for software development, AI, and analytics.",
          dataExposed: "Survey data shows systemic under‑protection of non‑production and AI training environments in cloud pipelines.",
          impactOnProviders: "Indicates systemic under‑protection of non‑production and AI training environments in cloud pipelines.",
          impactOnUsers: "Increased probability that personal and regulated data ends up in less protected cloud AI environments where compromise or model memorization can drive regulatory, financial, and reputational harm.",
          severity: "MEDIUM"
        }
      ]
    },
    {
      category: "Google Gemini / Vertex AI Incidents", 
      icon: FiEyeOff,
      incidents: [
        {
          date: "December 8, 2025",
          title: "GeminiJack Zero‑Click Data‑Exfiltration Vulnerability",
          description: "Researchers showed that an indirect prompt injection embedded in a shared Google Doc, calendar invite, or email could trigger Gemini Enterprise (and previously Vertex AI Search) to execute hidden instructions when using RAG over corporate data, exfiltrating search results to an attacker via an HTTP request in an HTML image tag.",
          dataExposed: "Corporate data in Gmail, Calendar, Docs and other connected sources.",
          impactOnGoogle: "Required fixes to how Gemini processes retrieved context and handles external instructions, and demonstrated that LLM‑based enterprise search can be turned into a covert data exfiltration channel.",
          impactOnUsers: "Corporate data could be leaked without any user click, bypassing traditional email and DLP controls and turning AI assistants themselves into the exfiltration vector.",
          severity: "CRITICAL"
        },
        {
          date: "2025", 
          title: "Gemini Vulnerabilities Exposing User Data and Enabling Phishing",
          description: "Multiple vulnerabilities including Gemini features tricked via prompt injection on websites to leak stored user information and location; vulnerabilities enabling injection of hidden instructions via HTML/CSS into emails for fraudulent notifications; flaws in Gemini command‑line coding assistant allowing prompt injection through 'context files'.",
          dataExposed: "Stored user information, location data, and potential credential theft through vishing attacks.",
          impactOnGoogle: "Necessitated blocking certain link rendering and strengthening defenses against prompt injection across Gemini products; highlighted insufficiency of guardrails focused only on visible text.",
          impactOnUsers: "Risk of leakage of personal and enterprise data, as well as social‑engineering and code‑supply‑chain compromises triggered by AI assistants acting on untrusted content in the cloud.",
          severity: "HIGH"
        },
        {
          date: "2024–2025",
          title: "Google AI Internal Leaks via Bug Bounty Findings",
          description: "Security researchers documented cases where Google AI systems exposed highly confidential internal protocol definitions and components due to subtle misconfigurations, indicating weaknesses in internal segregation of sensitive AI artefacts.",
          dataExposed: "Highly confidential internal protocol definitions and components.",
          impactOnGoogle: "Forced tightening of internal access controls and highlighted that sophisticated testers can extract non‑public implementation details of AI systems.",
          impactOnUsers: "While direct user data was not the focus, exposure of internal interfaces can accelerate development of exploits against production AI APIs and infrastructure.",
          severity: "MEDIUM"
        }
      ]
    },
    {
      category: "Meta (Facebook / Instagram / Llama / Meta AI) Data‑Use and Legal Risks",
      icon: FiUserX,
      incidents: [
        {
          date: "2025",
          title: "Training on Public Facebook and Instagram Data (EU Cases and Complaints)",
          description: "Meta announced plans to use public posts, profile information, comments, and likes from adult Facebook and Instagram users in the EU to train its AI models and power services like 'Meta AI,' leading to legal challenges and privacy‑advocacy complaints.",
          dataExposed: "Public posts, profile information, comments, and likes from EU users.",
          legalOutcome: "A Higher Regional Court in Germany allowed Meta to continue using public data under 'legitimate interest,' provided certain safeguards and opt‑out mechanisms, though broader challenges under GDPR and the Digital Markets Act remain ongoing.",
          impactOnMeta: "Reputational scrutiny, regulatory oversight, and potential constraints on training data practices in the EU.",
          impactOnUsers: "Even if data is notionally 'public,' individuals may have their posts used for AI training without explicit opt‑in, raising privacy, profiling, and downstream misuse concerns when models reproduce or infer sensitive details.",
          severity: "MEDIUM"
        }
      ]
    },
    {
      category: "Cross‑Cutting Risks Documented in Standards and Research",
      icon: FiShield,
      incidents: [
        {
          date: "2023–2025",
          title: "LLM‑Specific Sensitive‑Information Disclosure Risks (OWASP, CEDPO)",
          description: "OWASP's LLM risk category LLM06 describes how cloud LLMs can leak sensitive data through training data exposure, poor access control, or prompt exploitation. European data‑protection experts note that generative models can regenerate personal data from training data and amplify exposure by creating similar synthetic content.",
          dataExposed: "Personal data including names, addresses, health or financial details regenerated from training data.",
          impactOnProviders: "Adds pressure to implement data‑minimization, robust deletion, and pre‑training sanitization for cloud AI services.",
          impactOnUsers: "Even without a discrete 'breach,' using public cloud AI for regulated data can create ongoing confidentiality and compliance risk because the model may reproduce or infer personal information in unexpected contexts.",
          severity: "MEDIUM"
        }
      ]
    }
  ];

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'CRITICAL': return 'bg-red-100 text-red-800 border-red-200';
      case 'HIGH': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSeverityIcon = (severity) => {
    switch(severity) {
      case 'CRITICAL': return FiAlertTriangle;
      case 'HIGH': return FiShield;
      case 'MEDIUM': return FiServer;
      default: return FiAlertTriangle;
    }
  };

  return (
    <section className="py-20 bg-gray-50" itemScope itemType="https://schema.org/Article">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center justify-center p-2 bg-red-100 rounded-full mb-4">
            <SafeIcon icon={FiAlertTriangle} className="text-red-600 text-xl mx-2" />
            <span style={{
      color: 'rgb(153, 27, 27)'
    }} className="text-red-800 font-bold text-sm uppercase tracking-wide pr-2">Documented Factual Incidents</span>
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-6" itemProp="headline">
            The Hidden Dangers of Public AI Clouds
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto" itemProp="description">
            These are not hypothetical scenarios. They are <span className="font-bold text-slate-900">documented factual incidents</span> from a rapidly expanding list of security breaches. 
            This comprehensive analysis represents only a fraction of the ongoing security failures in public AI infrastructure - many more incidents go unreported or undisclosed.
          </p>
        </motion.div>

        {/* Compact Incidents Grid */}
        <div className="space-y-8">
          {incidents.map((category, categoryIndex) => (
            <motion.div
              key={categoryIndex}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: categoryIndex * 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
            >
              {/* Category Header */}
              <div className="bg-gradient-to-r from-slate-800 to-slate-700 p-4 border-b border-slate-600">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-teal-500/10 rounded-lg flex items-center justify-center border border-teal-500/20">
                    <SafeIcon icon={category.icon} className="text-teal-400 text-lg" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{category.category}</h3>
                </div>
              </div>

              {/* Compact Incident Cards Grid */}
              <div className="p-4">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {category.incidents.map((incident, incidentIndex) => (
                    <article 
                      key={incidentIndex} 
                      className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow relative"
                      itemScope 
                      itemType="https://schema.org/NewsArticle"
                    >
                      <meta itemProp="datePublished" content={incident.date} />
                      
                      {/* Severity Badge */}
                      <div className="absolute top-3 right-3">
                        <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-bold border ${getSeverityColor(incident.severity)}`}>
                          <SafeIcon icon={getSeverityIcon(incident.severity)} className="text-xs" />
                          <span>{incident.severity}</span>
                        </div>
                      </div>

                      {/* Incident Header */}
                      <div className="mb-3">
                        <div className="text-xs font-semibold text-gray-500 mb-1" itemProp="datePublished">
                          {incident.date}
                        </div>
                        <h4 className="text-sm font-bold text-slate-900 leading-tight mb-2" itemProp="headline">
                          {incident.title}
                        </h4>
                      </div>

                      {/* Compact Content */}
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs text-gray-600 leading-relaxed line-clamp-3" itemProp="articleBody">
                            {incident.description}
                          </p>
                        </div>

                        {/* Data Exposed - Compact */}
                        {incident.dataExposed && (
                          <div className="text-xs">
                            <span className="font-semibold text-red-700">Data Exposed: </span>
                            <span className="text-red-600">{incident.dataExposed}</span>
                          </div>
                        )}

                        {/* Impact on Users - Compact */}
                        <div className="text-xs">
                          <span className="font-semibold text-red-700">User Impact: </span>
                          <span className="text-red-600">{incident.impactOnUsers}</span>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Summary Warning */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="mt-16 bg-slate-900 rounded-xl p-8 border border-slate-800 shadow-2xl overflow-hidden relative"
        >
          <div className="absolute top-0 right-0 p-8 opacity-5">
            <SafeIcon icon={FiAlertTriangle} className="text-white text-9xl" />
          </div>
          
          <div className="relative z-10 grid md:grid-cols-3 gap-8 items-center">
            <div className="md:col-span-2">
              <h3 className="text-2xl font-bold text-white mb-4">
                "Just a Glitch" is Not an Acceptable Security Strategy
              </h3>
              <p className="text-gray-300 text-lg mb-4">
                The incidents documented above represent only the publicly disclosed breaches. Many more security failures in public AI clouds go unreported or remain undisclosed to protect corporate reputations.
              </p>
              <p className="text-gray-400 text-sm">
                Each incident demonstrates fundamental architectural flaws in multi-tenant cloud AI systems that cannot be patched or fixed - they require complete architectural redesign.
              </p>
            </div>
            <div className="text-center md:text-right">
              <div className="inline-block text-left">
                <p className="text-teal-400 font-bold text-sm uppercase tracking-wider mb-1">The Reality</p>
                <p className="text-white font-bold text-xl">These Are Facts, Not Fears</p>
                <p className="text-gray-400 text-sm mt-2">Documented. Verifiable. Ongoing.</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSection;